import {
    CART_ADD_ITEM,
    CART_REMOVE_ITEM,
    CART_UPDATE_QUANTITY,
    CART_UPDATE_PORTION,
    CART_CLEAR,
    SAVE_PRODUCT_CART_REQUEST,
    SAVE_PRODUCT_CART_SUCCESS,
    SAVE_PRODUCT_CART_FAILURE,
    GET_PRODUCT_CART_REQUEST,
    GET_PRODUCT_CART_SUCCESS,
    GET_PRODUCT_CART_FAILURE,
    APPLY_REMOVE_PROMO_CODE_REQUEST,
    APPLY_REMOVE_PROMO_CODE_SUCCESS,
    APPLY_REMOVE_PROMO_CODE_FAILURE,
    PLACE_ORDER_REQUEST,
    PLACE_ORDER_SUCCESS,
    PLACE_ORDER_FAILURE,
    LOGOUT_SUCCESS,
    RESET_DATA,
    SEGMENT_LIST_SUCCESS
} from '../../actions/action-types';
import { getSegmentCartInfo } from '../../../utils/segmentCart';
import { getCartCurrency } from '../../../utils/cartCurrency';

const initialState = {
    cartItems: [],
    data: {},
    cartId: null,
    serverCartSummary: null,
    serviceCartSummary: null,
    saveCartLoading: false,
    saveCartError: null,
    saveCartRequestId: null,
    getCartLoading: false,
    getCartError: null,
    lastSaveCartPayload: null
};

const emptyCartState = {
    ...initialState,
    cartId: null,
    data: {},
    cartItems: []
};

const clearShoppingCart = (state) => ({
    ...emptyCartState,
    serviceCartSummary: state.serviceCartSummary || null,
});

const getItemId = (item) =>
    String(
        item?.product_variant_id ||
        item?.variant_id ||
        item?.product_id ||
        item?.variant?.id ||
        item?.product?.id ||
        item?.id ||
        item?.cart_item_id ||
        ''
    );

const isPlainObject = (value) =>
    value && typeof value === 'object' && !Array.isArray(value);

const parseJsonValue = (value) => {
    if (typeof value !== 'string') return value;

    try {
        return JSON.parse(value);
    } catch (error) {
        return value;
    }
};

const toNumber = (value, fallback = 0) => {
    const normalizedValue = Number(String(value ?? '').replace(/,/g, '').replace(/[^0-9.-]/g, ''));

    return Number.isFinite(normalizedValue) ? normalizedValue : fallback;
};

const getValue = (item, keys) => {
    const matchedKey = keys.find(
        (key) => item?.[key] !== null && item?.[key] !== undefined && item?.[key] !== ''
    );

    return matchedKey ? item[matchedKey] : '';
};

const getNestedObject = (source, keys) => {
    if (!isPlainObject(source)) return {};

    const value = getValue(source, keys);
    const parsedValue = parseJsonValue(value);

    if (Array.isArray(parsedValue)) {
        return parsedValue.find(isPlainObject) || {};
    }

    return isPlainObject(parsedValue) ? parsedValue : {};
};

const getCartDataFromResponse = (payload) => {
    const parsedPayload = parseJsonValue(payload);
    const responseData = parseJsonValue(parsedPayload?.data);
    const data = Array.isArray(responseData)
        ? responseData.find(isPlainObject) || {}
        : responseData || {};
    const nestedDataCart = getNestedObject(data, [
        'cart',
        'cart_data',
        'cart_details',
        'cart_detail',
        'response_data'
    ]);
    const nestedPayloadCart = getNestedObject(parsedPayload, [
        'cart',
        'cart_data',
        'cart_details',
        'cart_detail',
        'response_data'
    ]);
    const nestedCartData = Object.keys(nestedDataCart).length
        ? nestedDataCart
        : nestedPayloadCart;

    if (isPlainObject(nestedCartData)) {
        return { ...data, ...nestedCartData };
    }

    return isPlainObject(data) ? data : {};
};

const getCartIdFromResponse = (payload) => {
    const cartData = getCartDataFromResponse(payload);
    const parsedPayload = parseJsonValue(payload);

    return getValue(cartData, ['cart_id', 'cartId', 'id']) || getValue(parsedPayload, ['cart_id', 'cartId']);
};

const getProductDetailsFromCartData = (cartData) => {
    const productDetails =
        cartData?.product_details ||
        cartData?.product_detail ||
        cartData?.products ||
        cartData?.items ||
        cartData?.cart_items ||
        cartData?.cart_products ||
        cartData?.cart_product_details ||
        cartData?.response_data ||
        cartData?.order_details ||
        cartData?.details;

    const parsedProductDetails = parseJsonValue(productDetails);

    return Array.isArray(parsedProductDetails) ? parsedProductDetails : null;
};

const isEmptyProductDetailsPayload = (payload = {}) => {
    const productDetails = parseJsonValue(payload?.product_details);

    return Array.isArray(productDetails) && productDetails.length === 0;
};

const mergeVerifiedCartContext = (cartData, requestPayload) => {
    const data = { ...cartData };
    const receipt = parseJsonValue(cartData?.receipt);
    const contextKeys = [
        ['segment_id', 'segmentId', 'segment'],
        ['business_segment_id', 'businessSegmentId', 'store_id', 'storeId', 'branch_id'],
        ['merchant_id', 'merchantId'],
    ];

    // Persist the verified context even when the server omits it from the response.
    contextKeys.forEach((keys) => {
        const value = getValue(cartData, keys) || getValue(receipt, keys) || getValue(requestPayload, keys);
        if (value !== '') data[keys[0]] = value;
    });

    return data;
};

const mergeResponseCartItems = (responseCartItems, existingCartItems, cartCurrency) => {
    if (!responseCartItems) {
        return cartCurrency
            ? existingCartItems.map((item) => ({ ...item, currency: cartCurrency }))
            : existingCartItems;
    }

    return responseCartItems.map((item) => {
        const existingItem = existingCartItems.find(
            (cartItem) => getItemId(cartItem) === getItemId(item)
        );
        const responseItem = normalizeItem(item);
        const existingPrice = Number(existingItem?.price || 0);
        const responsePrice = Number(responseItem.price || 0);
        const mergedItem = {
            ...existingItem,
            ...responseItem,
            currency: cartCurrency || responseItem.currency || existingItem?.currency || '',
            name:
                responseItem.name && responseItem.name !== 'Product'
                    ? responseItem.name
                    : existingItem?.name || responseItem.name,
            price: responsePrice > 0 ? responsePrice : existingPrice,
            image:
                responseItem.image && responseItem.image !== 'assets/images/thumbs/product-two-img1.png'
                    ? responseItem.image
                    : existingItem?.image || responseItem.image,
            portion: item.portion === undefined
                ? existingItem?.portion ?? responseItem.portion
                : responseItem.portion,
            segment_id: responseItem.segment_id || existingItem?.segment_id,
            business_segment_id: responseItem.business_segment_id || existingItem?.business_segment_id,
            unit: responseItem.unit || existingItem?.unit,
            merchant_id: responseItem.merchant_id ?? existingItem?.merchant_id,
            empty_bottle_count: item.empty_bottle_count ?? item.emptyBottleCount ?? existingItem?.empty_bottle_count,
            arr_option_id: item.arr_option_id ?? item.arrOptionId ?? existingItem?.arr_option_id,
        };

        return normalizeItem(mergedItem);
    });
};

const normalizeItem = (item) => {
    const product = item?.product || item?.product_detail || {};
    const variant = item?.variant || item?.variant_detail || item?.product_variant || {};

    return {
        id: getItemId(item),
        product_variant_id:
            item?.product_variant_id ||
            item?.variant_id ||
            item?.product_id ||
            variant?.id ||
            product?.id ||
            item?.id,
        name:
            item?.name ||
            item?.title ||
            item?.product_name ||
            item?.product_title ||
            product?.name ||
            product?.title ||
            variant?.name ||
            variant?.title ||
            'Product',
        price: toNumber(
            item?.price ??
            item?.product_price ??
            item?.selling_price ??
            item?.product_selling_price ??
            item?.product_cost ??
            item?.final_price ??
            item?.discounted_price ??
            item?.unit_price ??
            variant?.price ??
            variant?.selling_price ??
            product?.price ??
            0
        ),
        quantity: toNumber(item?.quantity || item?.qty || item?.cart_quantity || 1, 1),
        image:
            item?.image ||
            item?.product_cover_image ||
            item?.product_image ||
            item?.thumbnail ||
            variant?.image ||
            product?.image ||
            product?.product_cover_image ||
            'assets/images/thumbs/product-two-img1.png',
        currency: getCartCurrency(item) || getCartCurrency(product),
        rating: item?.rating || product?.rating,
        segment_id: getValue(item, ['segment_id', 'segmentId', 'segment']),
        business_segment_id: getValue(item, [
            'business_segment_id',
            'businessSegmentId',
            'store_id',
            'storeId',
            'branch_id'
        ]),
        portion: item?.portion || null,
        unit: item?.unit || item?.weight_unit || '',
        merchant_id: getValue(item, ['merchant_id', 'merchantId']) || null,
        empty_bottle_count: item?.empty_bottle_count ?? item?.emptyBottleCount ?? null,
        arr_option_id: item?.arr_option_id ?? item?.arrOptionId ?? null,
        options: item?.options || ''
    };
};

export const cartReducer = (state = initialState, { type, payload, meta }) => {
    switch (type) {
        case SEGMENT_LIST_SUCCESS: {
            const info = getSegmentCartInfo(payload);
            if (!info.present || state.saveCartLoading || state.getCartLoading) return state;
            const serviceCartSummary = info.servicePresent ? info.service : state.serviceCartSummary;
            if (!info.shoppingPresent) return { ...state, serviceCartSummary };
            if (!info.shopping) return { ...clearShoppingCart(state), serviceCartSummary };
            const currentId = state.cartId || state.serverCartSummary?.cart_id;
            const cartChanged = currentId && String(currentId) !== String(info.shopping.cart_id);
            return {
                ...(cartChanged ? clearShoppingCart(state) : state),
                serverCartSummary: info.shopping,
                serviceCartSummary,
            };
        }
        case SAVE_PRODUCT_CART_REQUEST:
            return {
                ...state,
                saveCartLoading: true,
                saveCartError: null,
                saveCartRequestId: meta?.requestId || null,
                lastSaveCartPayload: payload || null
            };
        case SAVE_PRODUCT_CART_SUCCESS: {
            if (meta?.requestId && state.saveCartRequestId !== meta.requestId) return state;
            const cartData = getCartDataFromResponse(payload);
            const responseCartItems = getProductDetailsFromCartData(cartData);
            const cartId = getCartIdFromResponse(payload) || state.cartId || state.serverCartSummary?.cart_id ||
                (meta?.verifyCart ? state.lastSaveCartPayload?.cart_id : null);

            const requestedEmptyCart = Array.isArray(meta?.cartItems)
                ? meta.cartItems.length === 0
                : isEmptyProductDetailsPayload(state.lastSaveCartPayload);

            if ((!responseCartItems && requestedEmptyCart) ||
                (Array.isArray(responseCartItems) && responseCartItems.length === 0) ||
                (meta?.verifyCart && !responseCartItems?.length)) {
                return {
                    ...clearShoppingCart(state),
                    saveCartLoading: false,
                    saveCartError: null
                };
            }

            return {
                ...state,
                data: meta?.verifyCart
                    ? mergeVerifiedCartContext(cartData, state.lastSaveCartPayload)
                    : cartData || state.data,
                cartId,
                serverCartSummary: null,
                cartItems: mergeResponseCartItems(responseCartItems,
                    meta?.verifyCart ? [] : meta?.cartItems || state.cartItems, getCartCurrency(cartData)),
                saveCartLoading: false,
                saveCartError: null,
                saveCartRequestId: null
            };
        }
        case SAVE_PRODUCT_CART_FAILURE:
            if (!meta?.authenticationFailed && meta?.requestId && state.saveCartRequestId !== meta.requestId) return state;
            return {
                ...(meta?.authenticationFailed ? emptyCartState : meta?.verifyCart ? clearShoppingCart(state) : state),
                saveCartLoading: false,
                saveCartError: payload || true,
                saveCartRequestId: null,
                lastSaveCartPayload: null
            };
        case GET_PRODUCT_CART_REQUEST:
            return {
                ...state,
                getCartLoading: true,
                getCartError: null
            };
        case GET_PRODUCT_CART_SUCCESS: {
            const cartData = getCartDataFromResponse(payload);
            const responseCartItems = getProductDetailsFromCartData(cartData);
            const cartId = getCartIdFromResponse(payload) || state.cartId || state.serverCartSummary?.cart_id;

            if (Array.isArray(responseCartItems) && responseCartItems.length === 0) {
                return {
                    ...clearShoppingCart(state),
                    getCartLoading: false,
                    getCartError: null
                };
            }

            return {
                ...state,
                data: cartData || state.data,
                cartId,
                serverCartSummary: null,
                cartItems: mergeResponseCartItems(responseCartItems, state.cartItems, getCartCurrency(cartData)),
                getCartLoading: false,
                getCartError: null
            };
        }
        case GET_PRODUCT_CART_FAILURE:
            return {
                ...state,
                getCartLoading: false,
                getCartError: payload || true
            };
        case APPLY_REMOVE_PROMO_CODE_REQUEST:
            return {
                ...state,
                saveCartLoading: true,
                saveCartError: null,
                lastSaveCartPayload: payload || null
            };
        case APPLY_REMOVE_PROMO_CODE_SUCCESS: {
            const cartData = getCartDataFromResponse(payload);
            const responseCartItems = getProductDetailsFromCartData(cartData);
            const cartId = getCartIdFromResponse(payload) || state.cartId || state.serverCartSummary?.cart_id;

            if (Array.isArray(responseCartItems) && responseCartItems.length === 0) {
                return {
                    ...clearShoppingCart(state),
                    saveCartLoading: false,
                    saveCartError: null
                };
            }

            return {
                ...state,
                data: {
                    ...state.data,
                    ...cartData
                },
                cartId,
                serverCartSummary: null,
                cartItems: mergeResponseCartItems(responseCartItems, state.cartItems, getCartCurrency(cartData)),
                saveCartLoading: false,
                saveCartError: null
            };
        }
        case APPLY_REMOVE_PROMO_CODE_FAILURE:
            return {
                ...state,
                saveCartLoading: false,
                saveCartError: payload || true
            };
        case PLACE_ORDER_REQUEST:
            return {
                ...state,
                saveCartLoading: true,
                saveCartError: null,
                lastSaveCartPayload: payload || null
            };
        case PLACE_ORDER_SUCCESS:
            return {
                ...clearShoppingCart(state)
            };
        case PLACE_ORDER_FAILURE:
            return {
                ...state,
                saveCartLoading: false,
                saveCartError: payload || true
            };
        case CART_ADD_ITEM: {
            const item = normalizeItem(payload);

            if (!item.id) {
                return state;
            }

            const existingItem = state.cartItems.find((cartItem) => getItemId(cartItem) === item.id);

            if (existingItem) {
                return {
                    ...state,
                    cartItems: state.cartItems.map((cartItem) =>
                        getItemId(cartItem) === item.id
                            ? {
                                ...cartItem,
                                ...(item.portion && item.portion !== cartItem.portion
                                    ? { portion: item.portion, price: item.price }
                                    : {}),
                                quantity: Number(cartItem.quantity || 0) + item.quantity,
                            }
                            : cartItem
                    )
                };
            }

            return {
                ...state,
                cartItems: [...state.cartItems, item]
            };
        }
        case CART_REMOVE_ITEM: {
            const nextCartItems = state.cartItems.filter((item) => getItemId(item) !== String(payload));

            if (nextCartItems.length === 0) {
                return {
                    ...clearShoppingCart(state)
                };
            }

            return {
                ...state,
                cartItems: nextCartItems
            };
        }
        case CART_UPDATE_QUANTITY:
            return {
                ...state,
                cartItems: state.cartItems.map((item) =>
                    getItemId(item) === String(payload?.id)
                        ? { ...item, quantity: Math.max(1, Number(payload?.quantity || 1)) }
                        : item
                )
            };
        case CART_UPDATE_PORTION:
            return {
                ...state,
                cartItems: state.cartItems.map((item) =>
                    getItemId(item) === String(payload?.id)
                        ? { ...item, portion: payload.portion, price: payload.price }
                        : item
                )
            };
        case CART_CLEAR:
            return clearShoppingCart(state);
        case LOGOUT_SUCCESS:
        case RESET_DATA:
            return {
                ...emptyCartState
            };
        default:
            return state;
    }
};
